import React from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';
import classNames from 'classnames';

import FormContainer from '../../../FormContainer';
import SearchForm from '../../../SearchForm';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';

import usePropertyForm from '../../../../../forms/loan/property';
import PropertyService from '../../../../../services/PropertyService';
import LoanService from '../../../../../services/LoanService';
import { useUiContext } from '../../../../../context/UiContext';
import SearchService from '../../../../../services/SearchService';
import useUserRole from '../../../../../hooks/useUserRole';

const tabs = [
  {
    label: 'Property',
  },
  {
    label: 'Taxes',
  },
];

export default function Property({
  loanId,
  isVisible,
  refetchLoan,
  switchToNextSection,
  readOnly,
  loan,
}) {
  const { showToast } = useUiContext();

  const {
    data: propertyData,
    refetch,
    isFetched,
  } = useQuery({
    queryKey: ['loanProperty', loanId],
    queryFn: () => LoanService.getLoanProperty(loanId),
    enabled: !!loanId && !readOnly,
  });

  const { isLender } = useUserRole();

  const readOnlyProperty = loan?._property ? [{ ...loan._property }] : null;

  const property = readOnly ? readOnlyProperty : propertyData;

  const isPropertyCreated =
    (isFetched || readOnly) && property?.length > 0 && loanId;

  const { mutate: createProperty, isPending: isPropertyCreating } = useMutation(
    {
      mutationFn: (values) =>
        PropertyService.createProperty({ formValues: values, loanId }),
      onSuccess: () => {
        showToast({
          text: 'Property created successfully',
          type: 'success',
        });
        refetchLoan();
        refetch();
        switchToNextSection();
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    }
  );

  const { mutateAsync: updatePropertyMutation, isPending: isPropertyUpdating } =
    useMutation({
      mutationFn: (values) => {
        return PropertyService.updateProperty({
          formValues: values,
          propertyId: property?.[0]?.id,
        });
      },
      onSuccess: () => {
        showToast({
          text: 'Property updated successfully',
          type: 'success',
        });
        refetch();
        refetchLoan();
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const propertyForm = usePropertyForm(
    isPropertyCreated ? updatePropertyMutation : createProperty,
    property?.[0],
    readOnly
  );

  return (
    <div>
      <div className="flex justify-between mb-4">
        <div>{isPropertyCreated && <AddOrEditPageTabs tabs={tabs} />}</div>
        <div>
          {!isPropertyCreated && !isLender && (
            <SearchForm
              onResultSelect={async (result) => {
                try {
                  await LoanService.updateLoan({
                    loanId,
                    formValues: { ...loan, property_id: result.id },
                  });
                  refetchLoan();
                  refetch();
                } catch (error) {
                  console.log(error);
                  showToast({
                    text:
                      error?.response?.data?.message || 'Something went wrong',
                    type: 'error',
                  });
                }
              }}
              noResultsText="No properties found"
              placeholder="Search Property"
              queryFn={SearchService.searchProperty}
              queryKey="propertySearch"
              formatSearchResult={(result) =>
                `${result.address_line_1}, ${result.address_line_2}, ${result.city}, ${result.state}`
              }
            />
          )}
        </div>
      </div>

      <div
        className={classNames(
          'p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700',
          { 'mb-10': !readOnly }
        )}
      >
        <div
          className={classNames({
            'mb-10': !readOnly,
          })}
        >
          <FormContainer
            noBillComId
            isVisible={isVisible}
            form={propertyForm}
            readOnly={readOnly}
            isSubmitting={isPropertyCreating || isPropertyUpdating}
          />
        </div>
      </div>
    </div>
  );
}
